/* eslint-disable no-console */
/* eslint-disable unicorn/prefer-module */

/**
 * ============================================================================
 * SERVICIO CENTRALIZADO DE TIPOS DE CAMBIO
 * ============================================================================
 *
 * Única fuente de verdad para UF, Dólar Chile y Dólar Perú.
 *
 * Estrategia de carga:
 *   - Desarrollo (localhost, `npm start`): fetch online (vía src/setupProxy.js, sin CORS)
 *     a los mismos endpoints de producción → si falla, JSON local como backup CON AVISO
 *     visible en pantalla (getBackupStatus/10.177) — nunca en silencio.
 *   - Test (jest/jsdom, NODE_ENV=test): JSON local directo, sin fetch (hermético).
 *   - Producción: fetch online → si falla → JSON local como backup (sin aviso; detrás del
 *     propio dominio de prod, gateado por hostname wecast/visiona).
 *
 * Sin fallbacks hardcodeados: siempre usa el último valor real disponible.
 *
 * USO:
 *   - Módulos puros (calculateVentas, etc.): importar funciones directamente
 *   - Componentes React: usar useExchangeRates() que wrappea este service
 */

// ─── Cache singleton ─────────────────────────────────────────────────────────

const cache = {
  uf: null, // { "YYYY-MM-DD": number }
  dolarChile: null, // { "YYYY-MM-DD": number }
  dolarPeru: null, // { "YYYY-MM-DD": number }
  // Arrays de fechas ordenadas descendente, pre-computados al cargar el cache.
  // Evitan Object.keys(mapa).sort() en cada lookup — era el cuello de botella
  // en toggles "Ver en dólares" con cientos de opps.
  ufSortedDesc: [],
  dolarChileSortedDesc: [],
  dolarPeruSortedDesc: [],
  loaded: false,
  loadingPromise: null, // Para evitar cargas concurrentes
  // Solo se llena en dev server real (ver esDevServerReal); null en producción y en test.
  // { uf: {usado, hasta}, dolarChile: {...}, dolarPeru: {...} }
  backup: null,
};

// Recomputa los arrays de fechas ordenadas descendente desde los mapas actuales.
// Se llama una sola vez al cargar/reemplazar el cache, no en cada lookup.
const rebuildSortedKeys = () => {
  cache.ufSortedDesc = cache.uf ? Object.keys(cache.uf).sort().reverse() : [];
  cache.dolarChileSortedDesc = cache.dolarChile ? Object.keys(cache.dolarChile).sort().reverse() : [];
  cache.dolarPeruSortedDesc = cache.dolarPeru ? Object.keys(cache.dolarPeru).sort().reverse() : [];
};

// Devuelve el array de fechas pre-ordenado descendente según el mapa pasado.
// Si el mapa no es uno de los cacheados (edge case), cae a sort on-demand.
const sortedDescFor = (mapa) => {
  if (mapa === cache.uf) return cache.ufSortedDesc;
  if (mapa === cache.dolarChile) return cache.dolarChileSortedDesc;
  if (mapa === cache.dolarPeru) return cache.dolarPeruSortedDesc;
  return mapa ? Object.keys(mapa).sort().reverse() : [];
};

// ─── Helpers internos ────────────────────────────────────────────────────────

const isLocal = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// Dev server real (`npm start`) vs entorno de test (jest/jsdom, NODE_ENV=test): el fetch
// online de desarrollo (ver cargarDesdeOnlineLocal) SOLO corre en el dev server real. En test
// no hay servidor detrás de src/setupProxy.js — un fetch ahí sería una llamada de red real
// contra jsdom (no determinística, rompe la hermeticidad de la línea base dorada). En test,
// localhost sigue leyendo el JSON local directo, igual que siempre.
const esDevServerReal = () => isLocal() && process.env.NODE_ENV !== "test";

// Última fecha (string "YYYY-MM-DD") de un mapa { fecha: valor }, o null si está vacío.
const ultimaFechaDe = (mapa) => {
  const fechas = Object.keys(mapa || {});
  return fechas.length > 0 ? fechas.sort().pop() : null;
};

/**
 * Convierte array JSON [{ fecha, valor }] a mapa { "YYYY-MM-DD": number }
 */
const arrayToMap = (data, parseAsFloat = false) => {
  const map = {};
  (data || []).forEach((item) => {
    const val = parseAsFloat ? parseFloat(item.valor) : item.valor;
    // Número de control para el chequeo: descarta entradas <= 0 o no numéricas
    // (el feed escribe "0.00" en días sin dato). Se guarda `val` tal cual viene
    // (float para Perú, string crudo para Chile/UF) para no romper consumidores.
    const num = parseFloat(item.valor);
    if (!isNaN(num) && num > 0) map[item.fecha] = val;
  });
  return map;
};

/**
 * Carga JSON local (fallback / desarrollo)
 */
const cargarDesdeLocal = async () => {
  const ufData = await import("../data/uf.json");
  const dolarCLData = await import("../data/dolarchile.json");
  const dolarPEData = await import("../data/dolarperu.json");

  return {
    uf: arrayToMap(ufData.default || ufData),
    dolarChile: arrayToMap(dolarCLData.default || dolarCLData),
    dolarPeru: arrayToMap(dolarPEData.default || dolarPEData, true),
  };
};

/**
 * Fetch individual con fallback a JSON local si falla (ej. CORS cross-origin).
 * Devuelve { map, usoFallback } — usoFallback permite avisar en pantalla (ver
 * cargarDesdeOnlineLocal) cuándo se está usando el respaldo desactualizado.
 */
const fetchConFallback = async (url, localImport, parseAsFloat = false) => {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return { map: arrayToMap(Array.isArray(data) ? data : [], parseAsFloat), usoFallback: false };
  } catch (err) {
    console.warn(`Fetch ${url} falló (${err.message}), usando JSON local`);
    const localData = await localImport();
    return { map: arrayToMap(localData.default || localData, parseAsFloat), usoFallback: true };
  }
};

/**
 * Carga desde APIs online (producción).
 * Usa paths relativos para recursos del mismo dominio (evita problemas de CORS/redirect).
 * Para el dominio ajeno, va directo a JSON local.
 */
const cargarDesdeOnline = async () => {
  const hostname = window.location.hostname;
  const isWecast = hostname.includes("wecast");
  const isVisiona = hostname.includes("visiona");

  const [ufRes, dolarChileRes, dolarPeruRes] = await Promise.all([
    // UF: solo relevante para Chile (wecast)
    isWecast
      ? fetchConFallback("/services/uf/uf.json", () => import("../data/uf.json"))
      : import("../data/uf.json").then((m) => ({ map: arrayToMap(m.default || m), usoFallback: false })),
    // Dólar Chile: path relativo si estamos en wecast, local si estamos en visiona
    isWecast
      ? fetchConFallback("/services/dolar/dolar.json", () => import("../data/dolarchile.json"))
      : import("../data/dolarchile.json").then((m) => ({ map: arrayToMap(m.default || m), usoFallback: false })),
    // Dólar Perú: path relativo si estamos en visiona, local si estamos en wecast
    isVisiona
      ? fetchConFallback("/services/dolar/dolar.json", () => import("../data/dolarperu.json"), true)
      : import("../data/dolarperu.json").then((m) => ({ map: arrayToMap(m.default || m, true), usoFallback: false })),
  ]);

  return { uf: ufRes.map, dolarChile: dolarChileRes.map, dolarPeru: dolarPeruRes.map };
};

/**
 * Carga online para el DEV SERVER REAL (localhost, `npm start`, fuera de test).
 * A diferencia de cargarDesdeOnline (producción, gateada por hostname wecast/visiona),
 * acá SIEMPRE se intentan las 3 series en vivo — localhost sirve Chile y Perú a la vez,
 * no un solo país por dominio. El fetch va contra src/setupProxy.js (dev-server-only,
 * nunca se bundlea a producción), que reenvía sin CORS a los mismos endpoints reales:
 *   /services/uf/uf.json            → https://wecast.cl/services/uf/uf.json
 *   /services/dolar/dolar.json      → https://wecast.cl/services/dolar/dolar.json
 *   /services/dolar-peru/dolar.json → https://visiona.pe/services/dolar/dolar.json
 * (el path "-peru" es una convención SOLO de este proxy local: wecast y visiona
 * exponen el mismo path "/services/dolar/dolar.json" cada uno en su propio dominio,
 * y localhost necesita las dos series al mismo tiempo, sin dominio que las distinga).
 * Si un fetch falla, cae al JSON local (fetchConFallback) y queda registrado en
 * cache.backup para que la pantalla avise (nunca en silencio, 10.177).
 */
const cargarDesdeOnlineLocal = async () => {
  const [ufRes, dolarChileRes, dolarPeruRes] = await Promise.all([
    fetchConFallback("/services/uf/uf.json", () => import("../data/uf.json")),
    fetchConFallback("/services/dolar/dolar.json", () => import("../data/dolarchile.json")),
    fetchConFallback("/services/dolar-peru/dolar.json", () => import("../data/dolarperu.json"), true),
  ]);

  cache.backup = {
    uf: { usado: ufRes.usoFallback, hasta: ufRes.usoFallback ? ultimaFechaDe(ufRes.map) : null },
    dolarChile: {
      usado: dolarChileRes.usoFallback,
      hasta: dolarChileRes.usoFallback ? ultimaFechaDe(dolarChileRes.map) : null,
    },
    dolarPeru: {
      usado: dolarPeruRes.usoFallback,
      hasta: dolarPeruRes.usoFallback ? ultimaFechaDe(dolarPeruRes.map) : null,
    },
  };

  return { uf: ufRes.map, dolarChile: dolarChileRes.map, dolarPeru: dolarPeruRes.map };
};

/**
 * Busca el valor más reciente en un mapa para una fecha dada.
 * Si no encuentra la fecha exacta, busca la fecha anterior más cercana.
 */
const lookupConFallback = (mapa, fechaStr) => {
  if (!mapa || !fechaStr) return null;
  const fecha = fechaStr.split("T")[0];
  const valor = mapa[fecha];
  if (valor !== undefined) return valor;

  // Buscar fecha anterior más cercana (array pre-ordenado, no sortea por llamada).
  const fechasDisponibles = sortedDescFor(mapa);
  for (const f of fechasDisponibles) {
    if (f <= fecha) return mapa[f];
  }
  return null;
};

/**
 * Obtiene el último valor conocido de un mapa (la fecha más reciente).
 */
const getLastValue = (mapa) => {
  if (!mapa) return null;
  const sortedDesc = sortedDescFor(mapa);
  if (sortedDesc.length === 0) return null;
  // desc → primer elemento es la fecha más reciente
  return mapa[sortedDesc[0]] || null;
};

// ─── API pública ─────────────────────────────────────────────────────────────

/**
 * Carga todos los tipos de cambio. Idempotente: si ya están cargados, retorna inmediatamente.
 * En dev server real (localhost, `npm start`) y en producción intenta online primero, si
 * falla usa JSON local como backup. En test (jest), JSON local directo.
 */
export const loadExchangeRates = async () => {
  if (cache.loaded) return;
  if (cache.loadingPromise) return cache.loadingPromise;

  cache.loadingPromise = (async () => {
    try {
      let data;
      if (esDevServerReal()) {
        data = await cargarDesdeOnlineLocal();
      } else if (isLocal()) {
        // Test (jest/jsdom, NODE_ENV=test): comportamiento intacto, JSON local directo.
        cache.backup = null;
        data = await cargarDesdeLocal();
      } else {
        cache.backup = null;
        try {
          data = await cargarDesdeOnline();
        } catch (err) {
          console.warn("Fetch online falló, usando JSON local como backup:", err.message);
          data = await cargarDesdeLocal();
        }
      }
      cache.uf = data.uf;
      cache.dolarChile = data.dolarChile;
      cache.dolarPeru = data.dolarPeru;
      rebuildSortedKeys();
      cache.loaded = true;
    } catch (error) {
      console.error("Error cargando tipos de cambio:", error);
      cache.uf = cache.uf || {};
      cache.dolarChile = cache.dolarChile || {};
      cache.dolarPeru = cache.dolarPeru || {};
      rebuildSortedKeys();
      cache.loaded = true;
    } finally {
      cache.loadingPromise = null;
    }
  })();

  return cache.loadingPromise;
};

/**
 * Retorna true si los datos ya están cargados.
 */
export const isLoaded = () => cache.loaded;

/**
 * Estado del respaldo de TC/UF en el dev server real (localhost, `npm start`).
 * null en producción y en test (no se trackea ahí — ver esDevServerReal). En dev server
 * real, { uf, dolarChile, dolarPeru }, cada uno { usado: boolean, hasta: "YYYY-MM-DD"|null }.
 * `usado: true` significa que el fetch online falló y se está usando el JSON
 * bundleado (desactualizado) como respaldo — la pantalla debe avisarlo (10.177).
 */
export const getBackupStatus = () => cache.backup;

// ─── Mapas crudos ────────────────────────────────────────────────────────────

export const getDolarMap = (pais) => (pais === "Chile" ? cache.dolarChile : cache.dolarPeru) || {};
export const getUFMap = () => cache.uf || {};

// ─── Lookups por fecha ───────────────────────────────────────────────────────

/**
 * Valor UF para una fecha. Fallback: último valor conocido.
 */
export const getUFValue = (fechaStr) => {
  const val = lookupConFallback(cache.uf, fechaStr);
  return val !== null ? val : getLastValue(cache.uf) || 0;
};

/**
 * Valor dólar para una fecha y país. Fallback: último valor conocido.
 */
export const getDolarValue = (fechaStr, pais) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  const val = lookupConFallback(mapa, fechaStr);
  return val !== null ? val : getDolarFallback(pais);
};

/**
 * Último valor real conocido del dólar para un país (dinámico, sin hardcodeo).
 */
export const getDolarFallback = (pais) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  return getLastValue(mapa) || 0;
};

/**
 * Fecha del último valor conocido del dólar para un país.
 */
export const getDolarLastDate = (pais) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  const sortedDesc = sortedDescFor(mapa);
  if (sortedDesc.length === 0) return null;
  return sortedDesc[0];
};

/**
 * Último valor real conocido de la UF (dinámico, sin hardcodeo).
 */
export const getUFFallback = () => getLastValue(cache.uf) || 0;

// ─── Dólar mensual ───────────────────────────────────────────────────────────

/**
 * Array de 12 valores: tipo de cambio dólar por mes para un país y año.
 * Para cada mes, toma el último valor disponible dentro del mes.
 * Si no hay datos para ese mes, busca la fecha anterior más cercana.
 */
export const calcularDolarMensual = (pais, year) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  const fallback = getDolarFallback(pais);
  const valores = Array(12).fill(fallback);

  if (!mapa || Object.keys(mapa).length === 0) return valores;

  for (let mes = 0; mes < 12; mes++) {
    const ultimoDiaMes = new Date(year, mes + 1, 0).getDate();

    // Buscar último valor disponible dentro del mes
    let valorEncontrado = null;
    for (let dia = ultimoDiaMes; dia >= 1; dia--) {
      const fechaStr = `${year}-${String(mes + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
      if (mapa[fechaStr] !== undefined) {
        valorEncontrado = mapa[fechaStr];
        break;
      }
    }

    // Si no hay datos en el mes, buscar fecha anterior más cercana
    if (valorEncontrado === null) {
      const ultimoDiaStr = `${year}-${String(mes + 1).padStart(2, "0")}-${String(ultimoDiaMes).padStart(2, "0")}`;
      const fechasDisponibles = sortedDescFor(mapa);
      for (const f of fechasDisponibles) {
        if (f <= ultimoDiaStr) {
          valorEncontrado = mapa[f];
          break;
        }
      }
    }

    valores[mes] = valorEncontrado || fallback;
  }

  return valores;
};

/**
 * Array de 12 valores: PROMEDIO del dólar de los días REALES del mes (no "último del mes" —
 * ver `calcularDolarMensual` arriba, que sigue siendo el criterio usado por el v2 fuera de
 * la Fase 3, hasta el paso 3.8). Si un mes no tiene ningún valor real registrado, cae al mismo
 * criterio "último disponible" que `calcularDolarMensual` para ESE mes — nunca deja un hueco.
 *
 * Promovido del script de medición de la Fase 2b (`scripts/fixtures-er/medicionTcFase2b.js`,
 * función `dolarPromedioMes`), mismo cálculo ya verificado contra la base dorada — ver
 * `docs/decisiones/2026-10-04-reingenieria-ebitda-fase2b-medicion-tc.md`. Es la función que usa
 * `convertirRegistroAUSD` (`src/pages/ERv2/conversionER.js`) para las partidas sin fecha propia
 * (Criterio 1, decisión de Ignacio 2026-10-04).
 */
export const calcularDolarMensualPromedio = (pais, year) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  // Chile guarda el string crudo del feed (ver arrayToMap, parseAsFloat=false) — parseFloat
  // explícito para que esta función SIEMPRE devuelva number (nunca mezclado con string, a
  // diferencia de `calcularDolarMensual`, que preserva el tipo crudo para no romper a sus
  // llamadores existentes).
  const ultimoDisponible = calcularDolarMensual(pais, year).map((v) => parseFloat(v) || 0);
  if (!mapa || Object.keys(mapa).length === 0) return ultimoDisponible;

  const valores = Array(12).fill(0);
  for (let mes = 0; mes < 12; mes++) {
    const prefix = `${year}-${String(mes + 1).padStart(2, "0")}-`;
    const delMes = Object.keys(mapa)
      .filter((f) => f.startsWith(prefix))
      .map((f) => parseFloat(mapa[f]))
      .filter((v) => !isNaN(v));
    valores[mes] = delMes.length > 0 ? delMes.reduce((a, b) => a + b, 0) / delMes.length : ultimoDisponible[mes];
  }
  return valores;
};

/**
 * Valor del dólar el día 10 (o más cercano anterior) de un mes dado.
 * Usado por el ER para cálculos de costos fijos.
 */
export const getDolarDia10 = (monthIndex, pais, year) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  const fallback = getDolarFallback(pais);

  if (!mapa || Object.keys(mapa).length === 0) return fallback;

  const fecha10 = `${year}-${String(monthIndex + 1).padStart(2, "0")}-10`;
  const valor = mapa[fecha10];
  if (valor !== undefined) return valor;

  const fechasDisponibles = sortedDescFor(mapa);
  for (const f of fechasDisponibles) {
    if (f <= fecha10) return mapa[f];
  }
  return fallback;
};

/**
 * Promedio del dólar durante un año calendario (bound por país).
 * Usa todos los valores registrados en el año desde el mapa cargado.
 * Si no hay datos del año, cae al último valor conocido (getDolarFallback).
 */
export const calcularDolarPromedioAno = (pais, year) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  if (!mapa) return getDolarFallback(pais);
  const yearStr = String(year);
  const valores = Object.entries(mapa)
    .filter(([fecha]) => fecha.startsWith(yearStr))
    .map(([, valor]) => valor);
  if (valores.length === 0) return getDolarFallback(pais);
  return valores.reduce((a, b) => a + b, 0) / valores.length;
};

// ─── Cross-rate ──────────────────────────────────────────────────────────────

/**
 * Cross-rate PEN/CLP para conversión entre países.
 * Usado por useCategoriaMoneda para convertir montos CLP a PEN.
 */
export const getCrossRate = () => {
  const rateCLP = getDolarFallback("Chile");
  const ratePEN = getDolarFallback("Perú");
  if (!rateCLP || !ratePEN) return 0;
  return ratePEN / rateCLP;
};

// ─── Conversión universal ────────────────────────────────────────────────────

/**
 * Convierte cualquier moneda a moneda local del país.
 * Reemplaza todas las funciones convertirAMonedaLocal dispersas.
 *
 * @param {number} valor - Monto a convertir
 * @param {string} monedaOriginal - "UF", "USD", "US$", "CLP", "PEN", etc.
 * @param {string} pais - "Chile" o "Perú"
 * @param {string} fechaStr - Fecha para cotización histórica (default: hoy)
 * @returns {number} Valor en moneda local
 */
export const convertirAMonedaLocal = (valor, monedaOriginal, pais, fechaStr = null) => {
  if (!valor) return 0;

  const num = parseFloat(valor) || 0;
  const monedaUpper = (monedaOriginal || "").toUpperCase();

  // UF solo Chile
  if (pais === "Chile" && monedaUpper.includes("UF")) {
    const fechaUsada = fechaStr || new Date().toISOString().split("T")[0];
    return Math.round(num * getUFValue(fechaUsada));
  }

  // Dólar → moneda local
  if (monedaUpper.includes("USD") || monedaUpper.includes("US$") || monedaUpper === "DÓLAR") {
    const fechaUsada = fechaStr || new Date().toISOString().split("T")[0];
    return Math.round(num * getDolarValue(fechaUsada, pais));
  }

  // Ya está en moneda local
  return num;
};

// ─── UTM (Chile) — motor paralelo, aditivo ─────────────────────────────────────
//
// Réplica del patrón UF/dólar (feed JSON online + fallback estático en src/data),
// PERO con dos diferencias deliberadas respecto a la UF:
//   1. La UTM es MENSUAL. getUTMValue(fecha) resuelve por el MES de la fecha (día 01),
//      no por el día exacto como getUFValue. El fallback busca el mes anterior más cercano.
//   2. Vive en su PROPIO cache y su PROPIO loader (loadUTM), sin engancharse al cache ni a
//      loadExchangeRates de UF/dólar. Así el agregado es puramente aditivo: para usar la UTM
//      el consumidor llama loadUTM() (igual que loadExchangeRates() para UF/dólar).
//
// Feed producción: /services/utm/utm.json (solo Chile/wecast). Fallback: src/data/utm.json.

const utmCache = {
  utm: null, // { "YYYY-MM-01": number }
  utmSortedDesc: [], // meses ordenados descendente, pre-computados al cargar
  loaded: false,
  loadingPromise: null,
};

// Normaliza cualquier fecha ("YYYY-MM-DD" o con hora) al día 01 de su mes.
const utmMesDe = (fechaStr) => (fechaStr ? fechaStr.split("T")[0].slice(0, 7) + "-01" : null);

/**
 * Carga la UTM. Idempotente. Paralelo a loadExchangeRates (no lo toca).
 * En producción sobre wecast usa el feed online con fallback al JSON local; en cualquier
 * otro caso (localhost o dominio ajeno) va directo al JSON local.
 */
export const loadUTM = async () => {
  if (utmCache.loaded) return;
  if (utmCache.loadingPromise) return utmCache.loadingPromise;

  utmCache.loadingPromise = (async () => {
    try {
      let map;
      if (isLocal()) {
        const local = await import("../data/utm.json");
        map = arrayToMap(local.default || local);
      } else {
        const isWecast = window.location.hostname.includes("wecast");
        map = isWecast
          ? (await fetchConFallback("/services/utm/utm.json", () => import("../data/utm.json"))).map
          : await import("../data/utm.json").then((m) => arrayToMap(m.default || m));
      }
      utmCache.utm = map;
    } catch (error) {
      console.error("Error cargando UTM:", error);
      utmCache.utm = utmCache.utm || {};
    } finally {
      utmCache.utmSortedDesc = utmCache.utm ? Object.keys(utmCache.utm).sort().reverse() : [];
      utmCache.loaded = true;
      utmCache.loadingPromise = null;
    }
  })();

  return utmCache.loadingPromise;
};

/**
 * Retorna true si la UTM ya está cargada.
 */
export const isUTMLoaded = () => utmCache.loaded;

/**
 * Mapa crudo de UTM { "YYYY-MM-01": number }.
 */
export const getUTMMap = () => utmCache.utm || {};

/**
 * Último valor real conocido de la UTM (dinámico, sin hardcodeo). Calcado de getUFFallback.
 */
export const getUTMFallback = () => {
  const sortedDesc = utmCache.utmSortedDesc;
  if (!utmCache.utm || sortedDesc.length === 0) return 0;
  return utmCache.utm[sortedDesc[0]] || 0;
};

/**
 * Valor UTM para una fecha, resuelto por MES (no por día — la UTM es mensual).
 * Si no hay dato para ese mes, cae al mes anterior más cercano; si tampoco, al último
 * valor conocido (getUTMFallback). Calcado de getUFValue con resolución mensual.
 */
export const getUTMValue = (fechaStr) => {
  const mapa = utmCache.utm;
  if (!mapa) return getUTMFallback();
  const mes = utmMesDe(fechaStr);
  if (mes && mapa[mes] !== undefined) return mapa[mes];
  if (mes) {
    for (const f of utmCache.utmSortedDesc) {
      if (f <= mes) return mapa[f];
    }
  }
  return getUTMFallback();
};
